import {
  FloatingFocusManager,
  OpenChangeReason,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
} from "@floating-ui/react";
import { Form, Link } from "@remix-run/react";
import { useEffect, useState } from "react";
import type { User } from "~/services/auth.server";
import type { Notification as NotificationType } from "~/types";
import DefaultProfilePic from "./DefaultProfilePic";
import { useSocket } from "~/context/socketContext";
import Notification from "~/components/Notification";

type NavBarUserIconProps = {
  user?: User | null;
  notifications?: NotificationType[];
};

export default function NavBarUserIcon({ user }: NavBarUserIconProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const socket = useSocket();

  useEffect(() => {
    if (!socket) return;
    if (user == null) return;

    socket.auth = { twitch_id: user.twitchId };
    socket.connect();

    function init(data: NotificationType[]) {
      setNotifications(data);
    }

    function newMessage(data: NotificationType[]) {
      setNotifications((prev) => [...data, ...prev]);
    }

    socket.on("new", newMessage);
    socket.on("init", init);

    return () => {
      socket.off("new", newMessage);
      socket.off("init", init);
    };
  }, [socket]);

  const handleOpenChange = (open: boolean, _event: Event, _reason: OpenChangeReason) => {
    setIsOpen(open);

    if (!open && user != null) {
      socket?.emit("read", "");
      setNotifications([]);
    }
  };

  const { refs, context, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: "top",
    middleware: [offset(12), shift(), flip({ mainAxis: true })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const headingId = useId();

  return (
    <div className="flex">
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        className="ml-0 lg:ml-12 relative"
      >
        {user ? (
          <img
            alt=""
            className="w-6 lg:w-12 rounded-full shadow-sm"
            src={user?.profilePic}
          />
        ) : (
          <div className="w-6 lg:w-12 h-6 lg:h-12">
            <DefaultProfilePic size="24" fill="#fefefe" />
          </div>
        )}
        {notifications.length > 0 ? (
          <div className="flex items-center justify-center text-sm size-5 absolute -top-1 -left-2 bg-card border-2 border-white rounded-full">
            {notifications.length < 100 ? notifications.length : "+99"}
          </div>
        ) : null}
      </button>
      {isOpen ? (
        <FloatingFocusManager context={context} modal={false}>
          <div
            className="border-2 border-black popover font-navbar-user text-xl ml-1 p-4 rounded-md text-white bg-[#553075]"
            ref={refs.setFloating}
            style={floatingStyles}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            <h3>
              omg <span className="inline font-semibold">{user?.username ?? "anon"}</span>{" "}
              hi!!
            </h3>
            <div className="bg-white/70 h-[1px] my-2"></div>
            {user ? (
              <Form method="post" action="/logout">
                <button className="font-semibold" type="submit">
                  Logout
                </button>
              </Form>
            ) : (
              <Link to="login" onClick={() => setIsOpen(false)} className="font-semibold">
                Login
              </Link>
            )}
            <div className="w-full h-[1px] bg-white/70 my-2"></div>
            <div className="flex flex-col">
              {notifications.length > 0 ? (
                notifications
                  .filter((_, idx) => idx < 3)
                  .map((notification) => (
                    <Notification
                      key={notification.id}
                      message={notification.message}
                      related_gremlin_streamer={notification.related_gremlin_streamer}
                      related_trade_id={notification.related_trade_id}
                      related_username={notification.related_username}
                    />
                  ))
              ) : (
                <p className="font-navbar-menu font-light text-lg">
                  No notifications pending
                </p>
              )}
              <Link prefetch="intent" to="/notifications" className="font-bold mt-2">
                Read all notifications
              </Link>
            </div>
          </div>
        </FloatingFocusManager>
      ) : null}
    </div>
  );
}
